import { MouseEvent, ReactNode } from "react";

import { Box, SxProps, Theme } from "@mui/material";
import { isFromNestedInteractiveElement } from "@shared";

interface Props {
  onClick: () => void;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

/**
 * A list row that opens its item on a mouse click anywhere outside its own links and buttons. It is
 * deliberately not a button (see RowActionButton, which keyboard and screen-reader users open the
 * item with); the base styles mirror MUI's ListItemButton, which these rows used to be.
 */
export function ClickableRow({ onClick, sx, children }: Props) {
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!isFromNestedInteractiveElement(event.target, event.currentTarget)) {
      onClick();
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={
        [
          {
            display: "flex",
            flexGrow: 1,
            alignItems: "center",
            minWidth: 0,
            px: 2,
            py: 1,
            cursor: "pointer",
            "&:hover": { bgcolor: "action.hover" },
          },
          ...(sx ? [sx] : []),
        ] as SxProps<Theme>
      }
    >
      {children}
    </Box>
  );
}
